import * as React from 'react';
import { useState } from 'react';
import {
    Avatar,
    Box,
    Button,
    Grid,
    Paper,
    TextField,
    Typography,
    useTheme,
    useMediaQuery,
    InputAdornment,
    IconButton,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../providers/ToastContext';
import { Controller, useForm } from 'react-hook-form';

interface IFormInputs {
    username: string;
    password: string;
}

const Login: React.FC = () => {
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { showToast } = useToast();

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<IFormInputs>({
        defaultValues: {
            username: '',
            password: '',
        },
    });
    const handleTogglePassword = () => {
        setShowPassword(prev => !prev);
    };

    const onSubmit = (data: IFormInputs) => {
        const { username, password } = data;

        if (username === 'admin' && password === 'password') {
            showToast('Login successful!', 'success');
            navigate('/merchants');
        } else {
            showToast('Invalid credentials!', 'error');
        }
    };

    return (
        <Grid
            container
            component="main"
            sx={{
                minHeight: '100vh',
                width: '100vw',
                backgroundColor: theme.palette.grey[100],
                overflow: 'hidden',
            }}
            justifyContent="center"
            alignItems="center"
        >
            <Paper
                elevation={6}
                sx={{
                    p: { xs: 3, sm: 6 },
                    borderRadius: 4,
                    width: '100%',
                    maxWidth: 400,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
                        <LockOutlinedIcon />
                    </Avatar>
                    <Typography component="h1" variant="h5">
                        Sign In
                    </Typography>
                    <Box
                        component="form"
                        onSubmit={handleSubmit(onSubmit)}
                        sx={{ mt: 3, width: '100%' }}
                    >
                        <Controller
                            name="username"
                            control={control}
                            rules={{ required: 'Username is required' }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    label="Username"
                                    name="username"
                                    autoComplete="username"
                                    margin="normal"
                                    error={!!errors.username}
                                    helperText={errors.username?.message}
                                />
                            )}
                        />
                        <Controller
                            name="password"
                            control={control}
                            rules={{
                                required: 'Password is required',
                                minLength: {
                                    value: 6,
                                    message:
                                        'Password must be at least 6 characters',
                                },
                            }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    label="Password"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="current-password"
                                    margin="normal"
                                    error={!!errors.password}
                                    helperText={errors.password?.message}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={
                                                        handleTogglePassword
                                                    }
                                                    edge="end"
                                                    aria-label="toggle password visibility"
                                                    tabIndex={-1} // prevent tab focus on icon button
                                                >
                                                    {showPassword ? (
                                                        <VisibilityOff />
                                                    ) : (
                                                        <Visibility />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, py: 1.5, fontWeight: 'bold' }}
                        >
                            Login
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Grid>
    );
};

export default Login;
